import { fireCmdbErrorAnalytics } from '@jira/servicedesk__insight-common-utils-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import {
	performCmdbGetRequest,
	performCmdbPutRequest,
} from '@jira/servicedesk__insight-common-utils-cmdb-client/src/performCmdbGetRequest.tsx';
import {
	getCmdbPermsTokenStatusUrl,
	getCmdbPermsRoleAssignmentUrl,
} from '@jira/servicedesk__insight-common-utils-cmdb-rest-urls/src/getCmdbConfigReferenceTypeUrl.tsx';
import { getDataManagerRoleAssignmentUrl } from '@jira/servicedesk__insight-common-utils-cmdb-rest-urls/src/getDataManagerProvisioningStatusUrl.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ActionApi, UpdateRoleAssignmentResponse } from './ActionApi.types.tsx';

type UpdateDataManagerRoleParams = {
	principalIds: string[];
	roleId: string;
	useUnitsEndpoint?: boolean;
};

type TokenStatusResponse = {
	isSuccessful: boolean;
};

export const UPDATE_ROLE_TOKEN_STATUS_TIMEOUT_MS = 5000;

export const UPDATE_ROLE_TOKEN_STATUS_INTERVAL_MS = 1000;

export const updateDataManagerRole =
	({ principalIds, roleId, useUnitsEndpoint }: UpdateDataManagerRoleParams): ActionApi =>
	async ({ setState, getState }, { workspaceId, createAnalyticsEvent }) => {
		const state = getState();

		if (state.dataManager.rolesUpdateLoading) {
			return;
		}

		setState({
			...state,
			dataManager: { ...state.dataManager, rolesUpdateLoading: true },
		});

		try {
			const requestBody = {
				principalIds,
				roleId,
				resourceType: 'workspace',
			};

			const updateRolesResponse: UpdateRoleAssignmentResponse = await performCmdbPutRequest(
				useUnitsEndpoint && fg('data-manager-units-onboarding')
					? getDataManagerRoleAssignmentUrl(workspaceId)
					: getCmdbPermsRoleAssignmentUrl(workspaceId),
				{
					body: JSON.stringify(requestBody),
				},
			);

			const pollTimeoutTime = new Date(Date.now() + UPDATE_ROLE_TOKEN_STATUS_TIMEOUT_MS).getTime();

			// poll the checking of the token status for 5 seconds as updating of
			// Identity role assignments have eventually consistency
			const tryPollTokenStatus = async (
				resolve: (value?: unknown) => void,
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				reject: (error?: any) => void,
			) => {
				if (Date.now() > pollTimeoutTime) {
					reject(new Error('Some role assignments have failed or is still in progress'));
					return;
				}
				try {
					const url = getCmdbPermsTokenStatusUrl(
						workspaceId,
						updateRolesResponse.tokens[updateRolesResponse.tokens.length - 1],
					);
					const updateRoleAssignmentToken: TokenStatusResponse = await performCmdbGetRequest(url);

					if (updateRoleAssignmentToken.isSuccessful) {
						resolve();
						return;
					}

					setTimeout(() => {
						tryPollTokenStatus(resolve, reject);
					}, UPDATE_ROLE_TOKEN_STATUS_INTERVAL_MS);
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (e: any) {
					// Ignore unexpected network/server error and quickly retry
					setTimeout(() => {
						tryPollTokenStatus(resolve, reject);
					}, UPDATE_ROLE_TOKEN_STATUS_INTERVAL_MS);
				}
			};

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			await new Promise((res, rej: (error?: any) => void) => tryPollTokenStatus(res, rej));

			setState({
				...getState(),
				dataManager: {
					...getState().dataManager,
					rolesUpdateLoading: false,
				},
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'updateDataManagerRole succeeded');
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				...getState(),
				dataManager: {
					...getState().dataManager,
					rolesUpdateLoading: false,
					rolesUpdateError: error,
				},
			});

			fireCmdbErrorAnalytics({
				meta: {
					id: 'updateDataManagerRole',
					packageName: 'jiraServicedeskInsightGlobalConfigurationStore',
					teamName: 'data-manager',
				},
				error,
			});

			throw error;
		}
	};
